import { yupResolver } from '@hookform/resolvers/yup';
import { alwaysRefetchApiQueryOptions, emptyBeneficiaryState } from 'BreetHelpers';
import { useFormStage, useUserCurrencyProps, useUserWalletApi, useWithdrawRequestContext } from 'BreetHooks';
import { selectMeta, selectSelectedBeneficiary, useAppSelector, useGetBeneficiariesQuery } from 'BreetRedux';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
	CreateWithdrawRequestFormSchema,
	type FIATWITHDRAWREQUESTSTAGETYPE,
	requestPaymentStageTitle,
} from '@/components/DialogWithdrawRequest/DialogWithdrawRequest.helpers';

import { FiatWithdrawRequestContext } from '../context/FormContext';

export const FiatWithdrawRequestProvider = ({ children }: { children?: ReactNode }) => {
	const { selectedWallet } = useWithdrawRequestContext();

	const isUsd = selectedWallet?.currency === 'USD';

	const currencyProps = useUserCurrencyProps();

	const walletCurrencyCode = selectedWallet?.currency ?? currencyProps?.code;

	const userWalletProps = useUserWalletApi(walletCurrencyCode);

	const { data: beneficiariesData } = useGetBeneficiariesQuery(undefined, { ...alwaysRefetchApiQueryOptions, skip: isUsd });

	const meta = useAppSelector(selectMeta);

	const beneficiaries = useMemo(() => beneficiariesData?.data ?? [], [beneficiariesData]);

	const beneficiary = useAppSelector(selectSelectedBeneficiary);

	const defaultNarration = useMemo(
		() => (beneficiaries.length > 1 ? '' : (beneficiary?.narration ?? '')),
		[beneficiaries.length, beneficiary]
	);

	const defaultSelectedBeneficiary = useMemo(
		() =>
			beneficiaries.length > 1
				? emptyBeneficiaryState
				: {
						accountName: beneficiary?.accountName ?? '',
						accountNumber: beneficiary?.accountNumber ?? '',
						bankName: beneficiary?.bankName ?? '',
						id: beneficiary?._id ?? '',
					},
		[beneficiaries.length, beneficiary]
	);

	const withdrawRequestHookForm = useForm({
		resolver: yupResolver(CreateWithdrawRequestFormSchema),
		defaultValues: {
			amount: '',
			selectedBeneficiary: defaultSelectedBeneficiary,
			narration: defaultNarration,
		},
		mode: 'onChange',
	});

	useEffect(() => {
		withdrawRequestHookForm.setValue('selectedBeneficiary', defaultSelectedBeneficiary);
		withdrawRequestHookForm.setValue('narration', defaultNarration);
	}, [defaultSelectedBeneficiary, defaultNarration, withdrawRequestHookForm]);

	const formStage = useFormStage<FIATWITHDRAWREQUESTSTAGETYPE>(
		'withdraw-funds',
		['withdraw-funds', 'add-beneficiary', 'withdraw-details', 'manage-beneficiaries'],
		requestPaymentStageTitle
	);

	const providerValue = useMemo(
		() => ({
			userWalletProps,
			meta,
			withdrawRequestHookForm,
			formStage,
			beneficiaries,
		}),
		[meta, userWalletProps, withdrawRequestHookForm, formStage, beneficiaries]
	);

	return <FiatWithdrawRequestContext.Provider value={providerValue}>{children}</FiatWithdrawRequestContext.Provider>;
};
